<template>
  <div v-if="shouldShowBlock" class="vacancy-import">
    <Preloader v-if="localLoading" />
    <template v-else>
      <div class="vacancy-import__top">
        <span class="vacancy-import__head">{{ $t('h1') }}</span>
      </div>
      <div>
        <template v-if="externalVacancies && externalVacancies.length">
          <IntegrationsVacancyItem
            v-for="(v,i) in integrations"
            :key="i"
            :edit="edit"
            :integration="v"
            :vacancy-id="vacancyId"
            :external-vacancy="getExternalVacancyByProviderId(v.id)"
          />
        </template>
      </div>
      <IntegrationsVacancyInviteMethod :vacancy="vacancy" />
    </template>
  </div>
</template>

<script>
import IntegrationsVacancyItem from '@/components/Integrations/IntegrationsVacancyItem';
import { computed, ref, toRefs } from '@vue/composition-api';
import useIntegrations from '@/compositions/useIntegrations';
import useValidation from '@/compositions/useValidation';
import Preloader from '@/components/Preloader';
import useVacancies from '@/compositions/useVacancies';
import IntegrationsVacancyInviteMethod from '@/components/Integrations/IntegrationsVacancyInviteMethod';

export default {
  name: 'IntegrationsVacancy',
  components: {
    IntegrationsVacancyInviteMethod,
    Preloader,
    IntegrationsVacancyItem,
  },
  props: {
    externalVacancies: {
      type: Array,
      required: true,
    },
    vacancyId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const { vacancyId, externalVacancies } = toRefs(props);
    const { integrations, fetchAll } = useIntegrations();
    const { localLoading, withApiErrorHandling } = useValidation();
    const { findVacancyById } = useVacancies();
    const vacancy = computed(() => findVacancyById(vacancyId.value));
    withApiErrorHandling(async () => {
      await fetchAll();
    });

    const rabotaIntegrations = computed(() => {
      return integrations.value.filter((integration) => {
        const code = integration.code?.toLowerCase();
        return code === 'rabota' || code === 'rabota.ru';
      });
    });

    const hasRabotaExternalVacancies = computed(() => {
      if (!externalVacancies.value || externalVacancies.value.length === 0) {
        return false;
      }
      const rabotaIntegrationIds = rabotaIntegrations.value.map((i) => i.id);
      return externalVacancies.value.some((ev) => rabotaIntegrationIds.includes(ev.integrationId));
    });

    const shouldShowBlock = computed(() => {
      return hasRabotaExternalVacancies.value;
    });

    function getExternalVacancyByProviderId(id) {
      return externalVacancies.value.find((v) => v.integrationId === id);
    }

    return {
      integrations: rabotaIntegrations,
      localLoading,
      edit: ref(false),
      getExternalVacancyByProviderId,
      vacancy,
      shouldShowBlock,
    };
  },
};
</script>

<style lang="scss" scoped>
  .vacancy-import {
    max-width: 31.625rem;
    background-color: #F6F6F6;
    padding: 1.625rem 1.875rem;
    font-size: 1.0625rem;
    &__top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
    }
    &__head {
      font-size: 1.5rem;
      line-height: 1.2;
      font-weight: 500;
    }
    &__edit {
      color: #3D97D9;
      border: none;
    }
    &__sign {
      padding-bottom: 0.625rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      margin-bottom: 0.5rem;
    }
  }
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    .vacancy-import {
      padding: 1rem 1.125rem 0.5rem 1.125rem;
      font-size: 1rem;
      &__top {
        margin-bottom: 1.5rem;
      }
      &__head {
        font-size: 1.125rem;
        line-height: 1;
      }
      &__sign {
        margin-bottom: 0;
      }
    }
  }
</style>

<i18n lang="yml" locale="en"></i18n>
<i18n lang="yml" locale="ru">
  h1: Импорт откликов
  title: Интеграция с Job-сайтами
  edit: Редактировать
</i18n>
